import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';

import { auth0Login } from 'shared/store/modules/auth/effects';
import { fetchAccounts } from 'shared/store/modules/accounts/effects';
import { fetchProspect, checkInquiryCapacity } from 'shared/store/modules/prospects/effects';
import { setActiveAccountId } from 'shared/store/modules/accounts/actions';
import { getProviderHostname } from 'shared/store/modules/provider/selectors';

import { useAuth0 } from '@auth0/auth0-react';
import { useLogout } from './useLogout';
import CUSTOM_AUTH0_ERRORS from 'shared/components/Auth0Errors';

export const CLAIMS_NAMESPACE = 'http://schemas.suncentral.net/claims';

// CS-5802: Mutex to prevent concurrent token refresh calls (race condition fix)
// This is module-level so it's shared across all hook instances
let refreshPromise = null;

export function useAuth0Session() {
  const dispatch = useAppDispatch();
  const [isRenewing, setIsRenewing] = useState(false);
  const history = useHistory();
  const hostname = useSelector(getProviderHostname);
  const { user, getAccessTokenSilently, isLoading, isAuthenticated, error } = useAuth0();
  const logout = useLogout();

  const isAdmin = useCallback(() => {
    if (!isAuthenticated) return false;
    return (
      user &&
      user[`${CLAIMS_NAMESPACE}/roles`]
        ?.join(' ')
        .toLowerCase()
        .includes('admin')
    );
  }, [isAuthenticated, user]);

  const getRole = useCallback(() => {
    if (!isAuthenticated) return null;
    return user[`${CLAIMS_NAMESPACE}/roles`][0];
  }, [isAuthenticated, user]);

  const onError = useCallback(
    e => {
      let errorType;

      if (CUSTOM_AUTH0_ERRORS[e.message]) {
        errorType = CUSTOM_AUTH0_ERRORS[e.message];
      } else if (CUSTOM_AUTH0_ERRORS[e.error]) {
        errorType = CUSTOM_AUTH0_ERRORS[e.error];
      } else {
        errorType = getRole() ? 'no_accounts' : 'no_role';
      }

      console.error(`Auth0 Warning: in useAuth0Session#onError: ${errorType}`, e);

      if (errorType === CUSTOM_AUTH0_ERRORS.login_required) {
        return logout({}, `/login`);
      } else {
        return logout({}, `/login?error=${encodeURI(e.error)}&error_type=${encodeURI(errorType)}`);
      }
    },
    [logout, getRole]
  );

  const renewSession = useCallback(
    (sessionCallback, cacheMode = 'off') => {
      // CS-5802: If a refresh is already in progress, wait for it to complete
      if (refreshPromise) {
        console.log('[Auth] Token refresh already in progress, waiting...');
        refreshPromise
          .then(response => {
            if (sessionCallback) sessionCallback(response);
          })
          .catch(() => {
            // Error already handled by the original refresh call
          });
        return;
      }

      setIsRenewing(true);
      if (isAuthenticated) {
        console.log('[Auth] Starting token refresh... [1]');
        refreshPromise = getAccessTokenSilently({
          cacheMode: cacheMode,
          detailedResponse: true
        })
          .then(response => {
            //  if (!response.access_token) {
            //   onError(new Error("No access token returned"))
            // }
            const expires = new Date();
            expires.setSeconds(expires.getSeconds() + response.expires_in);

            console.log(
              `[Auth] Token response: expires_in=${
                response.expires_in
              }s, calculated expires=${expires.toISOString()}, now=${new Date().toISOString()}`
            );

            dispatch(
              auth0Login(
                {
                  ...response,
                  role: getRole(),
                  hostname: hostname,
                  expires_in: response.expires_in,
                  expires: expires
                },
                user.sub
              )
            );

            console.log('[Auth] Token refresh completed successfully');
            if (sessionCallback) sessionCallback(response);
            return response;
          })
          .catch(err => {
            onError(err);
          })
          .finally(() => {
            refreshPromise = null;
            setIsRenewing(false);
          });
      }
    },
    [getAccessTokenSilently, dispatch, user, hostname, isAuthenticated, getRole, onError]
  );

  const startSession = useCallback(
    callback => {
      renewSession(async () => {
        if (!isAdmin()) {
          try {
            const accounts = await dispatch(fetchAccounts());

            if (accounts.length === 1) {
              dispatch(setActiveAccountId(accounts[0].id));
            }

            if (callback) callback(accounts);
          } catch (e) {
            onError(e);
          }
        }
      }, 'off');
    },
    [renewSession, dispatch, isAdmin, onError]
  );

  const automaticDispatchToRoute = useCallback(() => {
    renewSession(async () => {
      if (!isAdmin()) {
        try {
          const accounts = await dispatch(fetchAccounts());
          if (accounts.length === 1) {
            dispatch(setActiveAccountId(accounts[0].id));
            if (
              accounts[0].isStreamlinedProspect &&
              accounts[0].nextStepToComplete &&
              accounts[0].nextStepToComplete === 600
            ) {
              const prospect = await dispatch(fetchProspect(accounts[0].id));

              if (prospect) {
                const { associatedUtilityId, stateId } = prospect.payload;
                await dispatch(checkInquiryCapacity(null, associatedUtilityId, stateId))
                  .then(data => {
                    if (data?.payload?.data) {
                      const { programsAndCapacityAvailable } = data.payload.data;
                      if (!programsAndCapacityAvailable) {
                        history.push('/signup/review?addToWaitlist=true');
                      }
                    }
                  })
                  .catch(error => {
                    console.error('Error caught in automaticDispatchToRoute', error);
                  });
              }
            }
          } else {
            history.push('/accounts');
          }
        } catch (e) {
          onError(e);
        }
      }

      history.push('/');
    });
  }, [dispatch, isAdmin, history, renewSession, onError]);

  return {
    automaticDispatchToRoute,
    startSession,
    renewSession,
    user,
    error,
    isLoading,
    isAuthenticated,
    isAdmin,
    getRole,
    isRenewing,
    onError
  };
}
